// Deal Pipeline spec §4.1 / §4.3 — TanStack Query hooks for Deals + workflow.
//
// All mutation hooks invalidate the relevant list / detail queries. The
// Kanban drag-and-drop relies on `useUpdateDealSubState` and
// `useUpdateDealStage` which apply optimistic updates and roll back on
// error so the card never appears stuck on the source lane while the
// stage machine validates.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { dealsApi } from "@/api/deals";
import { ApiError } from "@/api/client";
import type { PagedResult } from "@/types/api";
import type {
  AdvanceStageRequest,
  AdvanceSubStateRequest,
  ArchiveRequest,
  AssignB1Request,
  ChangeCounterpartyRequest,
  CloneRequest,
  CreateDealRequest,
  DealDetailDto,
  DealListItemDto,
  DealListQuery,
  DeclineRequest,
  PatchDealRequest,
  ReassignRequest,
  UpdateDealRequest,
  WithdrawRequest,
} from "@/types/deals";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const dealKeys = {
  all: ["deals"] as const,
  lists: () => [...dealKeys.all, "list"] as const,
  list: (query: DealListQuery | undefined) =>
    [...dealKeys.lists(), query ?? {}] as QueryKey,
  detail: (id: string) => [...dealKeys.all, "detail", id] as const,
  activity: (id: string) => [...dealKeys.all, "activity", id] as const,
  assignmentHistory: (id: string) =>
    [...dealKeys.all, "assignment-history", id] as const,
  stageTransitions: (id: string) =>
    [...dealKeys.all, "stage-transitions", id] as const,
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useDealsList(query?: DealListQuery, enabled = true) {
  return useQuery({
    queryKey: dealKeys.list(query),
    queryFn: () => dealsApi.list(query),
    enabled,
  });
}

export function useDeal(id: string | undefined) {
  return useQuery({
    queryKey: dealKeys.detail(id ?? ""),
    queryFn: () => dealsApi.get(id!),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Cache helpers — keep optimistic updates DRY.
// ---------------------------------------------------------------------------

interface OptimisticListSnapshot {
  /** All [queryKey, value] tuples for active deal-list caches captured before
   *  the mutation, so rollback can restore them verbatim. */
  lists: Array<[QueryKey, PagedResult<DealListItemDto> | undefined]>;
}

function snapshotLists(
  qc: ReturnType<typeof useQueryClient>,
): OptimisticListSnapshot {
  const lists = qc.getQueriesData<PagedResult<DealListItemDto>>({
    queryKey: dealKeys.lists(),
  });
  return { lists };
}

function restoreLists(qc: ReturnType<typeof useQueryClient>, snap: OptimisticListSnapshot) {
  for (const [key, value] of snap.lists) qc.setQueryData(key, value);
}

function patchListsForDeal(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
  patch: Partial<DealListItemDto>,
) {
  const stageChanged = patch.stage !== undefined;
  qc.setQueriesData<PagedResult<DealListItemDto>>(
    { queryKey: dealKeys.lists() },
    (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        data: prev.data.map((d) => {
          if (d.id !== dealId) return d;
          const next = { ...d, ...patch };
          if (stageChanged && patch.stage !== d.stage) {
            // NOT a viewer-zone "today": StageEnteredDate is a DateTimeOffset
            // (DealListItemDto.StageEnteredDate) written server-side as
            // DateTimeOffset.UtcNow, and every reader parses it as an instant.
            // A day string here badges a just-moved card "1d" instead of "0d"
            // after 17:00 Phoenix. The UTC instant is the correct value.
            next.stageEnteredDate = new Date().toISOString();
          }
          return next;
        }),
      };
    },
  );
}

// ---------------------------------------------------------------------------
// Mutations — lifecycle
// ---------------------------------------------------------------------------

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDealRequest) => dealsApi.create(body),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      qc.setQueryData(dealKeys.detail(deal.id), deal);
    },
  });
}

export function useUpdateDeal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDealRequest) => dealsApi.update(id, body),
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

/**
 * Partial update — merges only the fields sent, unlike {@link useUpdateDeal}
 * (full-replacement PUT). Prefer this for targeted edits so unspecified
 * fields aren't wiped.
 */
export function usePatchDeal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchDealRequest) => dealsApi.patch(id, body),
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

/**
 * Drag-and-drop within a stage column. The mutation is optimistic — every
 * cached deal list patches immediately; on error the snapshot is restored
 * and the caller is responsible for surfacing the validation reason from
 * `error.errors` (toast). The detail cache is also reset on error so a
 * subsequent re-render reads ground truth.
 */
export function useUpdateDealSubState(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdvanceSubStateRequest) => dealsApi.advanceSubState(id, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: dealKeys.lists() });
      const snap = snapshotLists(qc);
      patchListsForDeal(qc, id, { subState: body.targetSubState });
      return snap;
    },
    onError: (_err, _vars, context) => {
      if (context) restoreLists(qc, context as OptimisticListSnapshot);
      qc.invalidateQueries({ queryKey: dealKeys.detail(id) });
    },
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

/** Drag-and-drop across stage columns. Same optimistic pattern as
 *  useUpdateDealSubState but updates both stage + sub-state in the cache. */
export function useUpdateDealStage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdvanceStageRequest) => dealsApi.advanceStage(id, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: dealKeys.lists() });
      const snap = snapshotLists(qc);
      patchListsForDeal(qc, id, {
        stage: body.targetStage,
        subState: body.targetSubState,
      });
      return snap;
    },
    onError: (_err, _vars, context) => {
      if (context) restoreLists(qc, context as OptimisticListSnapshot);
      qc.invalidateQueries({ queryKey: dealKeys.detail(id) });
    },
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

function useDealActionMutation<TArgs>(
  id: string,
  fn: (id: string, body: TArgs) => Promise<DealDetailDto>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TArgs) => fn(id, body),
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      qc.invalidateQueries({ queryKey: dealKeys.activity(id) });
      // Assign/reassign write the header AssignedB1Id/A1Id/C1Id fields and
      // sync the matching DealAssignment row server-side; the deal-header
      // 7-pill widget reads those rows, so its cache must be refreshed for
      // the pill to reflect the change.
      qc.invalidateQueries({ queryKey: ["deal-flow", "assignments", id] });
    },
  });
}

export const useDeclineDeal = (id: string) =>
  useDealActionMutation<DeclineRequest>(id, (i, b) => dealsApi.decline(i, b));
export const useWithdrawDeal = (id: string) =>
  useDealActionMutation<WithdrawRequest>(id, (i, b) => dealsApi.withdraw(i, b));
export const useArchiveDeal = (id: string) =>
  useDealActionMutation<ArchiveRequest>(id, (i, b) => dealsApi.archive(i, b));
// Clone answers with the NEW deal, so it is cached under the clone's id. The
// shared action mutation cached it under the source's id, and the source deal
// showed the clone's number and stage until a refetch (staging run,
// 2026-10-14). The source deal itself is not written.
export function useCloneDeal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CloneRequest) => dealsApi.clone(id, body),
    onSuccess: (clone) => {
      qc.setQueryData(dealKeys.detail(clone.id), clone);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}
export const useAssignB1 = (id: string) =>
  useDealActionMutation<AssignB1Request>(id, (i, b) => dealsApi.assignB1(i, b));
export const useChangeCounterparty = (id: string) =>
  useDealActionMutation<ChangeCounterpartyRequest>(id, (i, b) => dealsApi.changeCounterparty(i, b));
export const useReassignC1 = (id: string) =>
  useDealActionMutation<ReassignRequest>(id, (i, b) => dealsApi.reassignC1(i, b));
export const useReassignB1 = (id: string) =>
  useDealActionMutation<ReassignRequest>(id, (i, b) => dealsApi.reassignB1(i, b));
export const useReassignA1 = (id: string) =>
  useDealActionMutation<ReassignRequest>(id, (i, b) => dealsApi.reassignA1(i, b));

export function useRestoreDeal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dealsApi.restore(id),
    onSuccess: (deal) => {
      qc.setQueryData(dealKeys.detail(id), deal);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

/** Convenience: extract a server-validation message from an ApiError so
 *  the Kanban toast reads "Cannot advance: <reasons>" rather than a
 *  generic 4xx. */
export function describeApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = err.errors?.length ? err.errors.join("; ") : err.message;
    return detail || fallback;
  }
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
